/**
 * EntityView
 *
 * Shared entity display component used by both BuilderCanvas and SandboxPanel.
 * Routes to the appropriate view based on entityType.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { Bot, Beaker, Settings } from 'lucide-react';
import { useMeAgent } from '../../hooks/useAgents';
import { useModelLookup } from '../../hooks/useModels';
import { useMeSkills } from '../../hooks/useSkills';
import { useMeTools } from '../../hooks/useTools';

interface EntityViewProps {
  entityType: 'agent' | 'skill' | 'tool' | 'document';
  entityId: string;
  section?: string;
  highlights?: Array<{ entityType: string; entityId: string; field: string }>;
  /** Optional header actions (e.g. Test/Publish buttons in Builder) */
  actions?: ReactNode;
}

export default function EntityView({
  entityType,
  entityId,
  section,
  highlights = [],
  actions,
}: EntityViewProps) {
  if (entityType === 'agent') {
    return (
      <AgentView
        agentId={entityId}
        section={section}
        highlights={highlights}
        actions={actions}
      />
    );
  }

  return (
    <div className="flex flex-1 items-center justify-center">
      <div className="text-sm text-muted-foreground">
        {entityType} view coming soon
      </div>
    </div>
  );
}

interface AgentViewProps {
  agentId: string;
  section?: string;
  highlights: Array<{ entityType: string; entityId: string; field: string }>;
  actions?: ReactNode;
}

function AgentView({ agentId, section, highlights, actions }: AgentViewProps) {
  const { data: agent, isLoading } = useMeAgent(agentId);
  const modelLookup = useModelLookup();
  const { data: skillsData } = useMeSkills();
  const { data: toolsData } = useMeTools();

  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const skills = skillsData?.items ?? [];
  const tools = toolsData?.items ?? [];

  const agentSkills =
    agent?.skillIds
      ?.map((id) => skills.find((s) => s.skillId === id))
      .filter(Boolean) ?? [];

  const agentTools =
    agent?.toolIds
      ?.map((id) => tools.find((t) => t.toolId === id))
      .filter(Boolean) ?? [];

  useEffect(() => {
    if (section && sectionRefs.current[section]) {
      sectionRefs.current[section]?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [section, agent]);

  const isHighlighted = (field: string) =>
    highlights.some(
      (h) => h.entityId === agentId && h.field === field,
    );

  const highlightClass = (field: string) =>
    isHighlighted(field)
      ? 'ring-2 ring-primary-400 ring-offset-2 rounded-md transition-all duration-500'
      : '';

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-sm text-muted-foreground">Loading agent...</div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-sm text-muted-foreground">Agent not found</div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <Bot className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold text-foreground">{agent.name}</h2>
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Description */}
        {agent.description && (
          <div className={highlightClass('description')}>
            <div className="text-xs font-medium text-muted-foreground mb-1">
              Description
            </div>
            <p className="text-sm text-foreground">{agent.description}</p>
          </div>
        )}

        {/* Model Config */}
        <div className={highlightClass('model')}>
          <div className="text-xs font-medium text-muted-foreground mb-1">Model</div>
          <div className="text-sm text-foreground">
            {modelLookup[agent.modelId]?.displayName ?? agent.modelId}
          </div>
          {modelLookup[agent.modelId] && (
            <div className="mt-1 flex flex-wrap items-center gap-1">
              {modelLookup[agent.modelId].capabilities
                .filter((c) => c !== 'text')
                .map((cap) => (
                  <span key={cap} className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                    {cap.charAt(0).toUpperCase() + cap.slice(1)}
                  </span>
                ))}
              <span className="text-[10px] text-muted-foreground">
                {Math.round(modelLookup[agent.modelId].contextLimit / 1000)}K
              </span>
            </div>
          )}
          <div className="mt-1 flex gap-4 text-xs text-muted-foreground">
            <span>Temperature: {agent.temperature ?? 0.7}</span>
            <span>Max tokens: {agent.maxTokens ?? 4096}</span>
          </div>
        </div>

        {/* System Prompt */}
        <div className={highlightClass('systemPrompt')}>
          <div className="text-xs font-medium text-muted-foreground mb-1">
            System Prompt
          </div>
          <pre className="max-h-48 overflow-y-auto rounded-md border border-border bg-muted p-3 text-xs text-foreground whitespace-pre-wrap font-mono">
            {agent.systemPrompt}
          </pre>
        </div>

        {/* Skills */}
        <div
          ref={(el) => { sectionRefs.current['skills'] = el; }}
          className={highlightClass('skills')}
        >
          <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1">
            <Beaker className="h-3.5 w-3.5" />
            Skills ({agentSkills.length})
          </div>
          {agentSkills.length > 0 ? (
            <div className="space-y-1">
              {agentSkills.map((skill) => (
                <div
                  key={skill!.skillId}
                  className="rounded-md border border-border bg-card px-3 py-2 text-sm"
                >
                  <div className="font-medium text-foreground">
                    {skill!.name}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {skill!.description}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground italic">No skills attached</p>
          )}
        </div>

        {/* Tools */}
        <div
          ref={(el) => { sectionRefs.current['tools'] = el; }}
          className={highlightClass('tools')}
        >
          <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1">
            <Settings className="h-3.5 w-3.5" />
            Tools ({agentTools.length})
          </div>
          {agentTools.length > 0 ? (
            <div className="space-y-1">
              {agentTools.map((tool) => (
                <div
                  key={tool!.toolId}
                  className="rounded-md border border-border bg-card px-3 py-2 text-sm"
                >
                  <div className="font-medium text-foreground">
                    {tool!.name}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {tool!.description}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground italic">No tools attached</p>
          )}
        </div>

        {/* Metadata */}
        <div className="border-t border-border pt-3">
          <div className="text-xs text-muted-foreground space-y-0.5">
            <div>
              ID: <span className="font-mono">{agent.agentId}</span>
            </div>
            <div>Status: {agent.status}</div>
            {agent.latestVersion !== undefined && (
              <div>Latest version: v{agent.latestVersion}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
